import { SharedReportsResponse } from '../types/sharedReport'

// Reporting service base URL (mirrors reportService.ts).
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_REPORT_API_URL
  if (envUrl) return envUrl
  return import.meta.env.MODE === 'remote'
    ? 'https://ath-avi-reporting-uat-west.azurewebsites.net/api/v1'
    : 'http://localhost:3006/api/v1'
}

const authHeaders = (): Record<string, string> => {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = localStorage.getItem('auth-token')
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.error?.message || json?.message || `HTTP ${res.status}`)
  return json.data as T
}

export interface SaveResult { savedReportId: string; snapshotId: string; title: string; dataAsOf: string }
export interface ShareResult { shareId: string; url: string; expiresAt: string }
export interface SharePayload {
  customerId: string
  title?: string
  recipients: string[]
  message?: string
  expiryDays?: number
  requirePassword?: boolean
  password?: string
  delivery?: 'email' | 'copy'
}

class SharedReportService {
  private base = getApiUrl()

  async listSharedReports(customerId: string): Promise<SharedReportsResponse> {
    // History (recipients/views/status) changes as recipients open links — never serve a
    // cached body, or the table shows stale distribution/views after a share.
    const res = await fetch(`${this.base}/reports/saved?customerId=${encodeURIComponent(customerId)}`, {
      headers: authHeaders(), credentials: 'include', cache: 'no-store',
    })
    return unwrap<SharedReportsResponse>(res)
  }

  // In-app snapshot HTML (authenticated) — rendered into the viewer iframe via srcDoc.
  async getSnapshotHtml(snapshotId: string, customerId: string): Promise<string> {
    const res = await fetch(`${this.base}/reports/snapshots/${snapshotId}/view?customerId=${encodeURIComponent(customerId)}`, {
      headers: authHeaders(), credentials: 'include', cache: 'no-store',
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.text()
  }

  // Freeze a snapshot from a report config (same shape as /reports/generate).
  async save(config: Record<string, unknown>): Promise<SaveResult> {
    const res = await fetch(`${this.base}/reports/save`, {
      method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(config),
    })
    return unwrap<SaveResult>(res)
  }

  async share(snapshotId: string, payload: SharePayload): Promise<ShareResult> {
    const res = await fetch(`${this.base}/reports/snapshots/${snapshotId}/share`, {
      method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(payload),
    })
    return unwrap<ShareResult>(res)
  }

  async revoke(shareId: string, customerId: string): Promise<void> {
    const res = await fetch(`${this.base}/reports/shares/${shareId}/revoke`, {
      method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify({ customerId }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
  }

  async extend(shareId: string, customerId: string, days = 90): Promise<void> {
    const res = await fetch(`${this.base}/reports/shares/${shareId}/extend`, {
      method: 'PATCH', headers: authHeaders(), credentials: 'include', body: JSON.stringify({ customerId, days }),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
  }

  // In-app PDF download (authenticated) — fetch the blob then trigger a save.
  async downloadPdf(snapshotId: string, customerId: string, filename: string): Promise<void> {
    const res = await fetch(`${this.base}/reports/snapshots/${snapshotId}/pdf?customerId=${encodeURIComponent(customerId)}`, {
      headers: authHeaders(), credentials: 'include', cache: 'no-store',
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; document.body.appendChild(a); a.click()
    setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url) }, 1000)
  }
}

export const sharedReportService = new SharedReportService()
